// The single wipe of local storage on web, shared by the reset pipeline and the
// recovery shell's degraded sign-out. Zero runtime dependencies, since the shell
// bundles it.

export const SQLITE_DIRECTORY = "landmapper";

export const FIREBASE_AUTH_DATABASE = "firebaseLocalStorageDb";

export const RANGE_CACHE_DATABASE = "landmapper-range-cache";

// The service worker names every cache of app files with this prefix, and no cache with user data.
export const APP_FILES_CACHE_PREFIX = "landmapper-app-";

export type StorageWipeStep =
  | "release-database"
  | "service-workers"
  | "cache-storage"
  | "opfs"
  | "indexeddb"
  | "local-storage"
  | "session-storage";

export interface StorageWipeOptions {
  // Keep the user signed in across the wipe — true for every reason but logout. It
  // retains the auth database, the service worker registration and the app-file
  // caches, so the app still starts offline.
  preserveUserSession: boolean;
  // Release exclusive handles on the store. Only the live app holds any.
  releaseDatabase?: () => Promise<void>;
  onStepFailed?: (step: StorageWipeStep, error: unknown) => void;
}

const REMOVE_ATTEMPTS = 24;
const REMOVE_RETRY_MILLISECONDS = 100;

function isNotFound(error: unknown): boolean {
  return (error as { name?: string } | null)?.name === "NotFoundError";
}

async function entryNames(root: FileSystemDirectoryHandle): Promise<string[]> {
  const keys = (root as unknown as { keys: () => AsyncIterable<string> }).keys();
  const names: string[] = [];
  for await (const name of keys) names.push(name);
  return names;
}

function deleteDatabase(name: string): Promise<void> {
  return new Promise((resolve) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onerror = () => resolve();
    request.onblocked = () => resolve();
  });
}

async function databaseNames(): Promise<string[]> {
  // `databases()` reached Firefox in 126; the sync access handles this app
  // needs landed in 111, so the named floor is the whole wipe on that band.
  const names = new Set<string>([FIREBASE_AUTH_DATABASE, RANGE_CACHE_DATABASE]);
  if (typeof indexedDB.databases === "function") {
    for (const info of await indexedDB.databases()) {
      if (info.name) names.add(info.name);
    }
  }
  return [...names];
}

// Retries while the browser still holds the access handles. A surviving store
// means the next boot would open the previous user's data, so it is the one
// failure the wipe reports by throwing.
async function removeSqliteDirectory(root: FileSystemDirectoryHandle): Promise<void> {
  for (let attempt = 0; ; attempt++) {
    try {
      await root.removeEntry(SQLITE_DIRECTORY, { recursive: true });
      return;
    } catch (error) {
      if (isNotFound(error)) return;
      if (attempt >= REMOVE_ATTEMPTS) throw error;
      await new Promise((resolve) => setTimeout(resolve, REMOVE_RETRY_MILLISECONDS));
    }
  }
}

export async function wipeStorage(options: StorageWipeOptions): Promise<void> {
  const bestEffort = async (step: StorageWipeStep, run: () => Promise<void>) => {
    try {
      await run();
    } catch (error) {
      options.onStepFailed?.(step, error);
    }
  };

  await bestEffort("release-database", async () => {
    await options.releaseDatabase?.();
  });

  // Unreachable storage means there is no store to re-initialize over, so it is
  // reported and skipped. A root we *did* reach but cannot clear is terminal.
  let root: FileSystemDirectoryHandle | null = null;
  try {
    root = (await navigator.storage?.getDirectory?.()) ?? null;
  } catch (error) {
    options.onStepFailed?.("opfs", error);
  }
  // Held access handles (a wedged boot, or a second tab) must not cost the
  // user every other storage class — wipe the rest, then report.
  let sqliteFailure: unknown = null;
  if (root) {
    try {
      await removeSqliteDirectory(root);
    } catch (error) {
      sqliteFailure = error;
    }
  }

  if (!options.preserveUserSession && navigator.serviceWorker) {
    await bestEffort("service-workers", async () => {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    });
  }

  await bestEffort("cache-storage", async () => {
    const names = await caches.keys();
    const deleted = options.preserveUserSession
      ? names.filter((name) => !name.startsWith(APP_FILES_CACHE_PREFIX))
      : names;
    await Promise.all(deleted.map((name) => caches.delete(name)));
  });

  const reachableRoot = root;
  if (reachableRoot) {
    await bestEffort("opfs", async () => {
      const names = await entryNames(reachableRoot);
      await Promise.all(
        names
          .filter((name) => name !== SQLITE_DIRECTORY)
          .map((name) =>
            reachableRoot.removeEntry(name, { recursive: true }).catch((error: unknown) => {
              if (isNotFound(error)) return;
              options.onStepFailed?.("opfs", new Error(`removeEntry ${name}`, { cause: error }));
            }),
          ),
      );
    });
  }

  await bestEffort("indexeddb", async () => {
    const preserved = options.preserveUserSession ? FIREBASE_AUTH_DATABASE : null;
    const names = await databaseNames();
    await Promise.all(
      names.filter((name) => name !== preserved).map((name) => deleteDatabase(name)),
    );
  });

  await bestEffort("local-storage", async () => localStorage.clear());
  await bestEffort("session-storage", async () => sessionStorage.clear());

  // Last, so a surviving store never silently reports a successful wipe.
  if (sqliteFailure) throw sqliteFailure;
}
